import React, { useState, useEffect } from 'react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { businessAPI, ordersAPI } from '../../services/api';
import { format, subDays, eachDayOfInterval, eachMonthOfInterval, subMonths } from 'date-fns';
import { BarChart3, Users, ShoppingCart, TrendingUp, Euro, Store } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// ================================================================
// SVG CHART COMPONENTS (same as WholesalerAnalytics)
// ================================================================

const BarChart = ({ data, color = '#6366f1', height = 200, valuePrefix = '', noDataText = 'No data available' }) => {
  if (!data || data.length === 0) return <div className="text-center text-gray-400 py-10 text-sm">{noDataText}</div>;
  const values = data.map(d => d.value || 0);
  const maxVal = Math.max(...values, 1);
  const pad = { t: 25, r: 10, b: 35, l: valuePrefix === '€' ? 50 : 35 };
  const W = 500, H = height;
  const iW = W - pad.l - pad.r, iH = H - pad.t - pad.b;
  const barW = Math.min((iW / data.length) * 0.55, 40);
  const gap = iW / data.length;
  const yTicks = Array.from({ length: 5 }, (_, i) => Math.round((maxVal / 4) * i));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: `${H}px` }}>
      <defs>
        <linearGradient id={`bg-${color.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} /><stop offset="100%" stopColor={color} stopOpacity="0.35" />
        </linearGradient>
      </defs>
      {yTicks.map((v, i) => {
        const y = pad.t + iH - (v / maxVal) * iH;
        return (<g key={i}><line x1={pad.l} y1={y} x2={W - pad.r} y2={y} stroke="#f0f0f0" strokeWidth="1" />
          <text x={pad.l - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#9ca3af">{valuePrefix}{v >= 1000 ? `${(v/1000).toFixed(1)}k` : v}</text></g>);
      })}
      {data.map((d, i) => {
        const v = d.value || 0;
        const bH = (v / maxVal) * iH;
        const x = pad.l + i * gap + (gap - barW) / 2;
        const y = pad.t + iH - bH;
        return (<g key={i}>
          <rect x={x} y={y} width={barW} height={Math.max(bH, 1)} rx="3" fill={`url(#bg-${color.slice(1)})`} />
          {v > 0 && <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize="10" fontWeight="600" fill="#374151">{valuePrefix}{v >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(valuePrefix === '€' ? 0 : 0)}</text>}
          <text x={x + barW / 2} y={H - pad.b + 16} textAnchor="middle" fontSize="9.5" fill="#6b7280">{d.label}</text>
        </g>);
      })}
    </svg>
  );
};

const DonutChart = ({ data, size = 150, noOrdersText = 'No orders', ordersLabelText = 'orders', statusLabelFn }) => {
  const statusColors = { pending: '#f59e0b', confirmed: '#6366f1', processing: '#3b82f6', shipped: '#8b5cf6', delivered: '#10b981', cancelled: '#ef4444', in_progress: '#3b82f6' };
  const entries = Object.entries(data || {}).filter(([_, v]) => v > 0);
  const total = entries.reduce((s, [_, v]) => s + v, 0);
  if (total === 0) return <div className="text-center text-gray-400 py-6 text-sm">{noOrdersText}</div>;
  const R = 55, CX = 75, CY = 75, C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <div className="flex items-center justify-center gap-6 py-2">
      <svg width={size} height={size} viewBox="0 0 150 150">
        {entries.map(([status, count], i) => {
          const pct = count / total; const dash = C * pct; const gap = C - dash;
          const seg = <circle key={status} cx={CX} cy={CY} r={R} fill="none" stroke={statusColors[status] || '#94a3b8'}
            strokeWidth="22" strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-offset} strokeLinecap="butt" transform={`rotate(-90 ${CX} ${CY})`} />;
          offset += dash; return seg;
        })}
        <text x={CX} y={CY - 6} textAnchor="middle" fontSize="22" fontWeight="700" fill="#1f2937">{total}</text>
        <text x={CX} y={CY + 12} textAnchor="middle" fontSize="10" fill="#6b7280">{ordersLabelText}</text>
      </svg>
      <div className="space-y-1.5">
        {entries.map(([status, count]) => (
          <div key={status} className="flex items-center gap-2 text-xs">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: statusColors[status] || '#94a3b8' }} />
            <span className="text-gray-600 capitalize">{statusLabelFn ? statusLabelFn(status) : status.replace('_', ' ')}</span>
            <span className="font-semibold text-gray-800 ml-auto">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================================
// MAIN PAGE
// ================================================================
const BusinessAnalytics = () => {
  const { t } = useTranslation(['business', 'orders']);
  const [loading, setLoading] = useState(true);

  // Helper to get translated status labels
  const getStatusLabel = (status) => {
    const key = `orders:MyOrders.Statuses.${status}`;
    const translated = t(key);
    return translated !== key ? translated : status.replace('_', ' ');
  };
  const [orders, setOrders] = useState([]);
  const [timeRange, setTimeRange] = useState('30days');
  const [selectedWholesaler, setSelectedWholesaler] = useState('all');
  const [chartData, setChartData] = useState({ spending: [], orders: [] });

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (orders.length > 0) processChartData(orders); }, [timeRange, selectedWholesaler, orders]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const ordersRes = await ordersAPI.getMyOrders();
      let ordersList = [];
      if (ordersRes.data.data && Array.isArray(ordersRes.data.data)) ordersList = ordersRes.data.data;
      else if (Array.isArray(ordersRes.data)) ordersList = ordersRes.data;
      else if (ordersRes.data.orders && Array.isArray(ordersRes.data.orders)) ordersList = ordersRes.data.orders;
      setOrders(Array.isArray(ordersList) ? ordersList : []);
      processChartData(Array.isArray(ordersList) ? ordersList : []);
    } catch (err) { console.error('Analytics error:', err); setOrders([]); }
    finally { setLoading(false); }
  };

  const processChartData = (ordersList) => {
    if (!Array.isArray(ordersList) || ordersList.length === 0) { setChartData({ spending: [], orders: [] }); return; }
    const now = new Date();
    let startDate;
    switch (timeRange) {
      case '7days': startDate = subDays(now, 7); break;
      case '90days': startDate = subDays(now, 90); break;
      case 'year': startDate = subMonths(now, 11); break;
      default: startDate = subDays(now, 30);
    }
    let filtered = ordersList.filter(o => new Date(o.createdAt) >= startDate && o.status !== 'cancelled');
    if (selectedWholesaler !== 'all') filtered = filtered.filter(o => (o.wholesalerId?._id || o.wholesalerId) === selectedWholesaler);

    if (timeRange === 'year') {
      const months = eachMonthOfInterval({ start: startDate, end: now });
      const data = months.map(m => {
        const mo = filtered.filter(o => { const d = new Date(o.createdAt); return d.getMonth() === m.getMonth() && d.getFullYear() === m.getFullYear(); });
        return { label: format(m, 'MMM'), spending: mo.filter(o => !o.wholesalerId?.hidePricing).reduce((s, o) => s + (o.totals?.total || 0), 0), count: mo.length };
      });
      setChartData({ spending: data.map(d => ({ label: d.label, value: d.spending })), orders: data.map(d => ({ label: d.label, value: d.count })) });
    } else {
      const days = eachDayOfInterval({ start: startDate, end: now });
      const data = days.map(day => {
        const dayOrders = filtered.filter(o => format(new Date(o.createdAt), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd'));
        return { label: timeRange === '7days' ? format(day, 'EEE') : format(day, 'M/d'), spending: dayOrders.filter(o => !o.wholesalerId?.hidePricing).reduce((s, o) => s + (o.totals?.total || 0), 0), count: dayOrders.length };
      });
      const step = timeRange === '90days' ? 7 : timeRange === '30days' ? 2 : 1;
      const sampled = data.filter((_, i) => i % step === 0 || i === data.length - 1);
      setChartData({ spending: sampled.map(d => ({ label: d.label, value: d.spending })), orders: sampled.map(d => ({ label: d.label, value: d.count })) });
    }
  };

  // Computed metrics
  const getFilteredOrders = () => {
    let f = orders;
    if (selectedWholesaler !== 'all') f = f.filter(o => (o.wholesalerId?._id || o.wholesalerId) === selectedWholesaler);
    return f;
  };
  const getCompletedOrders = () => getFilteredOrders().filter(o => ['delivered', 'shipped'].includes(o.status));

  // Orders from wholesalers with hidden pricing — exclude from monetary calculations
  const isHiddenPricing = (o) => o.wholesalerId?.hidePricing === true;

  const filteredAll = getFilteredOrders();
  const completedOrders = getCompletedOrders();
  const pricedCompleted = completedOrders.filter(o => !isHiddenPricing(o));
  const totalSpent = pricedCompleted.reduce((s, o) => s + (o.totals?.total || 0), 0);
  const avgOrder = pricedCompleted.length > 0 ? totalSpent / pricedCompleted.length : 0;

  // Unique wholesalers from orders
  const wholesalers = [...new Map(
    orders.filter(o => o.wholesalerId?._id).map(o => [o.wholesalerId._id, { _id: o.wholesalerId._id, name: o.wholesalerId.companyName || 'Unknown' }])
  ).values()];

  // Status breakdown
  const statusBreakdown = {};
  filteredAll.forEach(o => { statusBreakdown[o.status] = (statusBreakdown[o.status] || 0) + 1; });

  // Top wholesalers by spending
  const getTopWholesalers = () => {
    const map = {};
    pricedCompleted.forEach(o => {
      const id = o.wholesalerId?._id;
      if (!id) return;
      if (!map[id]) map[id] = { name: o.wholesalerId.companyName || 'Unknown', orders: 0, spending: 0, type: o.wholesalerId.businessType || '' };
      map[id].orders++; map[id].spending += o.totals?.total || 0;
    });
    return Object.values(map).sort((a, b) => b.spending - a.spending).slice(0, 7);
  };

  // Top products by quantity ordered
  const getTopProducts = () => {
    const map = {};
    pricedCompleted.forEach(o => {
      (o.items || []).forEach(item => {
        const name = item.name || item.productName || 'Unknown';
        const key = name.toLowerCase();
        if (!map[key]) map[key] = { name, qty: 0, spending: 0, orders: 0 };
        map[key].qty += item.quantity || 0; map[key].spending += (item.price || item.unitPrice || 0) * (item.quantity || 0); map[key].orders++;
      });
    });
    return Object.values(map).sort((a, b) => b.spending - a.spending).slice(0, 7);
  };

  // Savings from discounts
  const totalSavings = pricedCompleted.reduce((s, o) => {
    const catalog = o.catalogDiscount?.amount || o.totals?.catalogDiscount || 0;
    const customer = o.discount?.amount || o.totals?.discount || 0;
    return s + catalog + customer;
  }, 0);

  const topWholesalers = getTopWholesalers();
  const topProducts = getTopProducts();

  if (loading) return (
    <div className="min-h-screen bg-gray-50"><Navbar />
      <div className="flex justify-center items-center py-32"><LoadingSpinner size="lg" /></div>
    <Footer /></div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-500" /> {t('analytics.title', 'Analytics')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{t('analytics.subtitle', 'Your purchasing insights and spending overview')}</p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border p-4 mb-6">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-2">{t('analytics.timeRange', 'Time Range')}</label>
                <div className="flex gap-1.5">
                  {[{ v: '7days', l: t('analytics.7days', '7 Ημέρες') }, { v: '30days', l: t('analytics.30days', '30 Ημέρες') }, { v: '90days', l: t('analytics.90days', '90 Ημέρες') }, { v: 'year', l: t('analytics.year', 'Χρόνος') }].map(r => (
                    <button key={r.v} onClick={() => setTimeRange(r.v)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        timeRange === r.v ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>{r.l}</button>
                  ))}
                </div>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-gray-500 block mb-2">{t('analytics.filterWholesaler', 'Filter by Wholesaler')}</label>
                <select value={selectedWholesaler} onChange={e => setSelectedWholesaler(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                  <option value="all">{t('analytics.allWholesalers', 'All Wholesalers')} ({wholesalers.length})</option>
                  {wholesalers.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
                </select>
              </div>
              {selectedWholesaler !== 'all' && (
                <button onClick={() => setSelectedWholesaler('all')}
                  className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg border">{t('common.clearFilter', 'Clear Filter')}</button>
              )}
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500">{t('analytics.totalOrders', 'Total Orders')}</span>
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"><ShoppingCart className="w-4 h-4 text-blue-500" /></div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{filteredAll.length}</p>
              <p className="text-xs text-gray-400 mt-1">{completedOrders.length} {t('analytics.completed', 'completed')}</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500">{t('analytics.totalSpent', 'Total Spent')}</span>
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center"><Euro className="w-4 h-4 text-green-500" /></div>
              </div>
              <p className="text-2xl font-bold text-green-600">€{totalSpent.toFixed(2)}</p>
              <p className="text-xs text-gray-400 mt-1">{t('analytics.fromCompleted', 'From completed orders')}</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500">{t('analytics.avgOrder', 'Avg Order')}</span>
                <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-purple-500" /></div>
              </div>
              <p className="text-2xl font-bold text-purple-600">€{avgOrder.toFixed(2)}</p>
              <p className="text-xs text-gray-400 mt-1">{t('analytics.perOrder', 'Per order')}</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500">{t('analytics.suppliers', 'Suppliers')}</span>
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><Store className="w-4 h-4 text-amber-500" /></div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{wholesalers.length}</p>
              <p className="text-xs text-gray-400 mt-1">
                {totalSavings > 0 && <span className="text-green-600 font-medium">€{totalSavings.toFixed(2)} {t('analytics.saved', 'εξοικονόμηση')}</span>}
                {totalSavings === 0 && t('analytics.uniqueWholesalers', 'Unique wholesalers')}
              </p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('analytics.spendingOverTime', 'Spending Over Time')}</h3>
              <BarChart data={chartData.spending} color="#10b981" valuePrefix="€" noDataText={t('analytics.noData', 'Δεν υπάρχουν δεδομένα')} />
            </div>
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('analytics.ordersOverTime', 'Orders Over Time')}</h3>
              <BarChart data={chartData.orders} color="#6366f1" noDataText={t('analytics.noData', 'Δεν υπάρχουν δεδομένα')} />
            </div>
          </div>

          {/* Order Status + Wholesaler Summary or At a Glance */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('analytics.orderStatus', 'Order Status')}</h3>
              <DonutChart data={statusBreakdown} noOrdersText={t('analytics.noOrders', 'Δεν υπάρχουν παραγγελίες')} ordersLabelText={t('analytics.ordersLabel', 'παραγγελίες')} statusLabelFn={getStatusLabel} />
            </div>

            {selectedWholesaler !== 'all' ? (
              <div className="bg-white rounded-xl border p-6 lg:col-span-2">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('analytics.wholesalerSummary', 'Wholesaler Summary')}</h3>
                {(() => {
                  const w = wholesalers.find(w => w._id === selectedWholesaler);
                  return w ? (
                    <div className="space-y-3">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm font-semibold text-blue-900">{w.name}</p>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <p className="text-lg font-bold text-gray-900">{filteredAll.length}</p>
                          <p className="text-xs text-gray-500">{t('analytics.orders', 'Orders')}</p>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <p className="text-lg font-bold text-green-600">€{totalSpent.toFixed(0)}</p>
                          <p className="text-xs text-gray-500">{t('analytics.spent', 'Spent')}</p>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <p className="text-lg font-bold text-purple-600">€{avgOrder.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{t('analytics.avgValue', 'Avg Value')}</p>
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
            ) : (
              <div className="bg-white rounded-xl border p-6 lg:col-span-2">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('analytics.atAGlance', 'At a Glance')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex justify-between items-center px-3 py-2.5 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">{t('analytics.cancellationRate', 'Cancellation Rate')}</span>
                    <span className="text-sm font-bold text-gray-900">
                      {filteredAll.length > 0 ? ((statusBreakdown.cancelled || 0) / filteredAll.length * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center px-3 py-2.5 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">{t('analytics.completionRate', 'Completion Rate')}</span>
                    <span className="text-sm font-bold text-green-600">
                      {filteredAll.length > 0 ? (completedOrders.length / filteredAll.length * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center px-3 py-2.5 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">{t('analytics.pendingOrders', 'Pending Orders')}</span>
                    <span className="text-sm font-bold text-yellow-600">{statusBreakdown.pending || 0}</span>
                  </div>
                  <div className="flex justify-between items-center px-3 py-2.5 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">{t('analytics.totalSavings', 'Total Savings')}</span>
                    <span className="text-sm font-bold text-green-600">€{totalSavings.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Top Wholesalers & Top Products */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">{t('analytics.topWholesalers', 'Top Wholesalers')}</h3>
              {topWholesalers.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">{t('analytics.noData', 'No data yet')}</p>
              ) : (
                <div className="space-y-3">
                  {topWholesalers.map((w, i) => {
                    const max = topWholesalers[0]?.spending || 1;
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${
                              i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-600' : 'bg-gray-200 text-gray-500'
                            }`}>{i + 1}</span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{w.name}</p>
                              <p className="text-xs text-gray-400">{w.orders} {t('analytics.ordersLabel', 'παραγγελίες')}</p>
                            </div>
                          </div>
                          <span className="text-sm font-bold text-green-600 flex-shrink-0 ml-2">€{w.spending.toFixed(0)}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden ml-8">
                          <div className="h-full bg-gradient-to-r from-emerald-400 to-green-500 rounded-full" style={{ width: `${(w.spending / max) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">{t('analytics.topProducts', 'Top Products')}</h3>
              {topProducts.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">{t('analytics.noData', 'No data yet')}</p>
              ) : (
                <div className="space-y-3">
                  {topProducts.map((p, i) => {
                    const max = topProducts[0]?.spending || 1;
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${
                              i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-600' : 'bg-gray-200 text-gray-500'
                            }`}>{i + 1}</span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                              <p className="text-xs text-gray-400">{p.qty} {t('analytics.units', 'μονάδες')} • {p.orders} {t('analytics.ordersLabel', 'παραγγελίες')}</p>
                            </div>
                          </div>
                          <span className="text-sm font-bold text-blue-600 flex-shrink-0 ml-2">€{p.spending.toFixed(0)}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden ml-8">
                          <div className="h-full bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full" style={{ width: `${(p.spending / max) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default BusinessAnalytics;